// Libraries and frameworks
import { RouteLocationNormalizedGeneric, RouteLocationAsRelativeGeneric } from "vue-router";
import { useLDClient } from "launchdarkly-vue-client-sdk";
import * as Sentry from "@sentry/vue";
import { ref, computed } from "vue";
import { defineStore } from "pinia";
// bapi-client
import { BAPI_COMMANDS } from "@/bapi-client/types/commands";
import type { CustomerAssociationExtended, UserMe } from "@/bapi-client/types/user";
import type { TermsAndConditions } from "@/bapi-client/types/terms-and-conditions";
import { BAPIError } from "@/bapi-client/errors";
import { useBapi } from "@/bapi-client";
// stores
import useNotificationStore from "@/stores/notifications";
import { useAnalyticsStore } from "@/stores/analytics";
import useTrackerStore from "@/stores/trackers";
import useFeatureStore from "@/stores/features";
// utils and composables
import { useTheme } from "@/composables/useTheme";
import { useTermsAndConditions } from "@/composables/termsAndConditions";
import { useAuthSession } from "@/composables/useAuthSession";
import dayjs from "dayjs";

export const useUserStore = defineStore("user", () => {
  // Store dependencies
  const notifier = useNotificationStore();
  const trackers = useTrackerStore();
  const featureStore = useFeatureStore();
  const ldClient = useLDClient();
  const { isLatestTermsAndConditionsAccepted } = useTermsAndConditions();

  // State
  const user = ref<UserMe>();
  const ssoProvider = ref<"okta" | "auth0" | undefined>();
  const userVerifiedAt = ref<string>();
  const company = ref<CustomerAssociationExtended>();
  const permissions = ref<string[]>([]);
  const customerViewEnabled = ref(false);
  const latestTermsAndConditions = ref<TermsAndConditions | undefined>(undefined);
  const acceptedLatestTermsAndConditions = ref<boolean>(false);

  // Computed properties
  const customerId = computed(() => company.value?.id ?? "");

  const hasDashboardPermissions = computed(() => {
    const dashboardPermissions = [
      "dashboard_widgets_dwelling_over_2_days",
      "dashboard_widgets_ap_status",
      "dashboard_widgets_cp_status_or_notified",
      "dashboard_widgets_ready_for_release",
    ];

    return dashboardPermissions.some((p) => permissions.value.includes(p));
  });

  const hasVisibilityAssetListPermission = computed(() => permissions.value.includes("visibility_asset_list"));
  const hasUniversalSearchPermission = computed(() => permissions.value.includes("visibility_universal_search"));

  const hasReportingPermissions = computed(() => {
    const reportingPermissions = ["reporting_report_builder", "reporting_forecasting"];

    return reportingPermissions.some((p) => permissions.value.includes(p));
  });

  const landingPage = computed<RouteLocationAsRelativeGeneric>(() => {
    // Terminal user case
    if (
      (company.value?.role_id === 7 || company.value?.role_id === 8) &&
      permissions.value.includes("terminal_terminal")
    ) {
      return { name: "terminalLoadList" };
    }

    // Dashboard permissions case
    if (hasDashboardPermissions.value) {
      return { name: "dashboard" };
    }

    // Assets list case
    if (hasVisibilityAssetListPermission.value) {
      return {
        name: "shipmentsDashboard",
        params: { customerId: customerId.value },
      };
    }

    // Waybill module case
    if (permissions.value.includes("waybilling_patterns")) {
      return {
        name: "patternsList",
        params: { customerId: customerId.value },
      };
    }

    // Reporting case
    if (hasReportingPermissions.value) {
      return {
        name: "reportBuilderDashboard",
        params: { customerId: customerId.value },
      };
    }

    // Analytics case
    const analyticsStore = useAnalyticsStore();
    const reportId = analyticsStore.views[0]?.url;
    if (reportId) {
      return {
        name: "advancedAnalytics",
        params: { customerId: customerId.value },
        query: { report: encodeURIComponent(reportId) },
      };
    }
    // Fallback case - user profile doesn't require specific permissions
    return { name: "userProfile" };
  });

  const waybillStatuses = computed<Record<number, string>>(() => {
    if (!company.value) {
      return {};
    }
    let statuses: Record<number, string>;
    switch (company.value.waybill_type_safe) {
      case "X":
        statuses = {
          0: "Waybilled",
          20: "Ingate",
          30: "Ramped",
          40: "In Transit",
          50: "Destination", //all 50 codes are deprecated, leaving per suggestion from Lucas
          60: "Deramped",
          80: "Notified",
          100: "Outgate",
        };
        break;
      case "R":
        statuses = {
          0: "Waybilled",
          10: "Origin",
          40: "In Transit",
          50: "Destination",
          70: "CP",
          90: "AP",
          100: "Complete",
        };
        break;
      default:
        statuses = {
          0: "Waybilled",
          10: "Origin",
          20: "Ingate",
          30: "Ramped",
          40: "In Transit",
          50: "Destination",
          60: "Deramped",
          70: "CP",
          80: "Notified",
          90: "AP",
          100: "Complete",
        };
        break;
    }
    return statuses;
  });

  const showTermsAndConditionsModal = computed(() => {
    if (featureStore.features.hasTermsAndConditions) {
      return !acceptedLatestTermsAndConditions.value && latestTermsAndConditions.value;
    }
    return false;
  });

  const showTermsAndConditionsCard = computed(() => {
    if (featureStore.features.hasTermsAndConditions) {
      return acceptedLatestTermsAndConditions.value && latestTermsAndConditions.value;
    }
    return false;
  });

  // Helper functions
  async function setUserCustomer(id: string) {
    if (!user.value) {
      console.warn("Attempted to set user customer with no user present.");
      return undefined;
    }

    if (!id) {
      console.warn("No customer ID provided.");
      return undefined;
    }

    const customer = user.value.companies.find((cust) => cust.id === id);
    if (!customer) {
      console.error("User does not have access to customer", customerId);
      Sentry.captureException(`User does not have access to customer ID ${id} `);
      notifier.setToast("danger", "You do not have access to the requested customer.");
      localStorage.removeItem("defaultCompany");
      return undefined;
    }

    company.value = customer ?? user.value.companies[0];
    setCustomerTableau(company.value);
    await getCustomerPermissions(company.value.id);
    localStorage.setItem("defaultCompany", JSON.stringify(company.value.id));

    // Need to make sure that the user is reidentified with the new customer for flag evaluation.
    await ldClient.identify({
      kind: "multi",
      user: {
        key: user.value?.user_id,
        name: user.value?.first_name + " " + user.value?.last_name,
        isAdmin: user.value?.is_admin,
        customerId: customerId.value,
      },
      customer: {
        key: company.value?.id,
        name: company.value?.name,
        type: company.value?.type,
      },
    });

    return company.value;
  }

  async function setUpAppEnvironment() {
    if (!company.value) {
      console.warn("Attempted to set up app environment without a company.");
      company.value = user.value?.companies[0];
    }

    // Load appropriate permissions based on customer view setting
    const cp = customerViewEnabled.value === true ? await enableCustomerView() : await disableCustomerView();
    if (!cp.success) {
      return cp;
    }
    // Set notification status based on permissions
    if (permissions.value.length || company.value?.integrations?.tableau?.views?.length) {
      notifier.status = "ready";
      notifier.lastResponseCode = 0;
    } else {
      notifier.status = "fault";
      notifier.lastResponseCode = 403;
    }
    // set up rudderstack and launchdarkly
    trackers.addIdentityTrackers();
    await ldClient.identify({
      kind: "multi",
      user: {
        key: user.value?.user_id,
        name: user.value?.first_name + " " + user.value?.last_name,
        isAdmin: user.value?.is_admin,
        customerId: customerId.value,
      },
      customer: {
        key: company.value?.id,
        name: company.value?.name,
        type: company.value?.type,
      },
    });

    // Set theme based on customer preferences
    if (company.value) {
      if (company.value.preferred_theme === "wabtec" || company.value.preferred_theme === "staging") {
        const themeValue = company.value?.preferred_theme === "wabtec" ? "wabtec" : "dev";
        useTheme().setTheme(themeValue);
        return { success: true };
      }
      localStorage.setItem("defaultTheme", company.value.preferred_theme);
      useTheme();
    }
    return { success: true };
  }

  function routeBasedOffOfEntitlements(to: RouteLocationNormalizedGeneric) {
    if (to.meta?.permissions) {
      if (!(to.meta.permissions as Array<string>).some((p) => permissions.value.includes(p))) {
        return landingPage.value;
      }
    }
    if (to.meta?.flag) {
      const featureStore = useFeatureStore();
      // if you don't have this feature, go to your landing page.
      if (!featureStore.features[to.meta.flag as keyof typeof featureStore.features]) {
        return landingPage.value;
      }
    }
    return to;
  }

  async function setTermsAndConditionsData() {
    const [isAccepted, termsAndConditions] = await isLatestTermsAndConditionsAccepted();

    acceptedLatestTermsAndConditions.value = isAccepted;
    latestTermsAndConditions.value = termsAndConditions;
  }
  // Actions

  // view as customer actions
  async function enableCustomerView(): Promise<{ success: boolean; error?: BAPIError }> {
    if (!user.value?.is_admin) {
      return { success: false, error: new BAPIError("User is not an admin.", 403) };
    }
    const cp = await useBapi(BAPI_COMMANDS.ADMIN_GET_CUSTOMER_PERMISSIONS, customerId.value);
    notifier.setLoading();
    if (!cp.success) {
      console.error("Failed to load customer permissions: ", cp.error);
      return cp;
    }
    permissions.value = cp.data.permissions.map((p) => p.name);
    return { success: true };
  }

  async function disableCustomerView(): Promise<{ success: boolean; error?: BAPIError }> {
    const allPermissions = await useBapi(BAPI_COMMANDS.USER_PERMISSIONS, customerId.value);
    if (!allPermissions.success) {
      console.error("Failed to load all permissions: ", allPermissions.error);

      return allPermissions;
    }
    permissions.value = allPermissions.data;
    return { success: true };
  }

  // Authentication actions
  async function loginUser(email: string, password: string): Promise<{ success: boolean; error?: BAPIError }> {
    const response = await useBapi(BAPI_COMMANDS.USER_LOGIN, {
      email,
      password,
    });
    if (!response.success) {
      console.error("Login failed: ", response.error.status);
      return response;
    }
    const { authExpiresAt, authToken } = useAuthSession();
    user.value = response.data;
    const now = dayjs().toISOString();
    authToken.value = response.data.token;
    authExpiresAt.value = dayjs(now).add(24, "hours").toISOString();
    userVerifiedAt.value = now;
    return { success: true };
  }

  async function logoutUser() {
    notifier.setLoading("Logging out");
    await useBapi(BAPI_COMMANDS.USER_LOGOUT);

    // Reset local state
    trackers.rudderstack?.resetIdentifier();

    Sentry.setUser(null);
    user.value = undefined;
    company.value = undefined;
    permissions.value = [];

    // Remove auth token
    localStorage.removeItem("authToken");
    localStorage.removeItem("authExpiresAt");

    notifier.setLoading();
    notifier.status = "ready";
  }

  async function verifyCurrentUser(): Promise<{ success: boolean; error?: BAPIError }> {
    const { authToken } = useAuthSession();

    const response = await useBapi(BAPI_COMMANDS.USER_GET_ME);

    if (!response.success) {
      console.error("Unable to verify user: ", response.error);
      localStorage.removeItem("authToken");
      return response;
    }

    user.value = response.data;
    authToken.value = response.data.token;
    const now = dayjs().toISOString();
    userVerifiedAt.value = now;

    try {
      setUser(response.data);
      return { success: true };
    } catch (e) {
      console.error("Failed to set user data: ", e);
      if (e instanceof Error) {
        notifier.setToast("danger", e.message);
      }
      return {
        success: false,
        error: new BAPIError("No customer associations found for user.", 422),
      };
    }
  }

  function setUser(authUser: UserMe) {
    user.value = authUser;
    localStorage.setItem("authToken", authUser.token);
  }

  async function getCustomerPermissions(id: string) {
    const allPermissions = await useBapi(BAPI_COMMANDS.USER_PERMISSIONS, id);
    if (allPermissions.success) {
      permissions.value = allPermissions.data;
    } else {
      console.error("Failed to load customer permissions: ", allPermissions.error);
    }
  }

  async function setCustomerTableau(customer: CustomerAssociationExtended) {
    if (customer.integrations?.tableau) {
      const analytics = useAnalyticsStore();
      analytics.token = customer.integrations.tableau.config.token ?? "";
      analytics.views = customer.integrations.tableau.views;
    } else {
      const analytics = useAnalyticsStore();
      analytics.token = "";
      analytics.views = [];
    }
  }
  // Expose store API
  return {
    // State
    user,
    company,
    permissions,
    customerViewEnabled,
    acceptedLatestTermsAndConditions,
    latestTermsAndConditions,
    ssoProvider,
    userVerifiedAt,

    // Getters
    customerId,
    hasDashboardPermissions,
    hasVisibilityAssetListPermission,
    hasUniversalSearchPermission,
    hasReportingPermissions,
    landingPage,
    waybillStatuses,
    showTermsAndConditionsModal,
    showTermsAndConditionsCard,

    // Actions
    loginUser,
    logoutUser,
    verifyCurrentUser,
    setUserCustomer,
    setUser,
    setUpAppEnvironment,
    routeBasedOffOfEntitlements,
    enableCustomerView,
    disableCustomerView,
    setTermsAndConditionsData,
  };
});
